import { ArrowDownIcon } from "@phosphor-icons/react/dist/csr/ArrowDown";
import { ArrowsDownUpIcon } from "@phosphor-icons/react/dist/csr/ArrowsDownUp";
import { ArrowUpIcon } from "@phosphor-icons/react/dist/csr/ArrowUp";
import { flexRender, type Table } from "@tanstack/react-table";
import { TableHead, TableRow } from "@volca/ui/components/ui/table";
import { cn } from "@volca/ui/lib/utils";
import {
  getPinnedEdgeClassName,
  getPinningStyles,
  HEAD_CLASSES,
  ROW_CLASSES,
} from "./styles";

interface SimpleTableHeaderRowsProps<TData> {
  table: Table<TData>;
  increaseHeaderPadding?: boolean;
}

export const SimpleTableHeaderRows = <TData,>({
  table,
  increaseHeaderPadding,
}: SimpleTableHeaderRowsProps<TData>) =>
  table.getHeaderGroups().map((headerGroup) => (
    <TableRow
      key={headerGroup.id}
      className={cn(ROW_CLASSES, "hover:bg-transparent")}
    >
      {headerGroup.headers.map((header) => {
        const canSort = header.column.getCanSort();
        const sorted = header.column.getIsSorted();
        const alignRight = header.column.columnDef.meta?.align === "right";
        const contentClassName = cn(
          "flex h-full w-full items-center gap-1 text-left",
          { "flex-row-reverse text-right": alignRight },
        );
        const content = (
          <>
            <div className="min-w-0">
              {flexRender(header.column.columnDef.header, header.getContext())}
            </div>
            {canSort && (
              <span aria-hidden className="shrink-0">
                {sorted === "asc" ? (
                  <ArrowUpIcon className="size-4" />
                ) : sorted === "desc" ? (
                  <ArrowDownIcon className="size-4" />
                ) : (
                  <ArrowsDownUpIcon className="size-4" />
                )}
              </span>
            )}
          </>
        );
        return (
          <TableHead
            key={header.id}
            aria-sort={
              sorted === "asc"
                ? "ascending"
                : sorted === "desc"
                  ? "descending"
                  : canSort
                    ? "none"
                    : undefined
            }
            style={{
              ...getPinningStyles(header.column, true),
              paddingTop: increaseHeaderPadding ? "15px" : "0px",
              paddingBottom: increaseHeaderPadding ? "10px" : "0px",
              height: increaseHeaderPadding ? "66px" : "",
            }}
            className={cn(
              HEAD_CLASSES,
              "bg-surface-raised select-none",
              getPinnedEdgeClassName(header.column),
              {
                "hover:bg-neutral-hovered": canSort,
                "bg-neutral": !!sorted,
              },
            )}
          >
            {canSort ? (
              <button
                type="button"
                onClick={header.column.getToggleSortingHandler()}
                className={cn(
                  contentClassName,
                  "cursor-pointer outline-none focus-visible:ring-3 focus-visible:ring-focus/50",
                )}
              >
                {content}
              </button>
            ) : (
              <div className={contentClassName}>{content}</div>
            )}
          </TableHead>
        );
      })}
    </TableRow>
  ));
